<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useHead } from '@unhead/vue'
import TopBar from './components/layout/TopBar.vue'
import AppHeader from './components/layout/AppHeader.vue'
import AppFooter from './components/layout/AppFooter.vue'
import ReassuranceDialog from './components/ui/ReassuranceDialog.vue'
import { useAuth } from '@/composables/useAuth'
import { useZendesk } from '@/composables/useZendesk'
import { setUnauthorizedHandler } from '@/services/http'
import { staticPageHref } from '@/router/products'
import type { Locale } from '@/i18n'

const { locale } = useI18n()
const route = useRoute()
const router = useRouter()
const { isAuthenticated, logout } = useAuth()

// Chatbot Zendesk : un seul widget à la fois selon l'authentification — widget
// « adhérent » si connecté, « prospect » sinon (swap au login/logout). Client uniquement.
useZendesk()

// `mounted` évite un mismatch d'hydratation : la session n'est pas connue au
// pré-rendu SSG. On n'adapte le chrome qu'une fois monté côté client.
const mounted = ref(false)
onMounted(() => {
  mounted.value = true
  // Session expirée / token invalide (401 terminal après refresh) : on purge la
  // session et on renvoie vers la connexion localisée.
  setUnauthorizedHandler(() => {
    logout()
    router.push(staticPageHref('login', locale.value as Locale))
  })
})

/** L'espace personnel a son propre chrome (header membre + sidebar) : on masque
 *  alors le header public. La topbar et le footer restent communs. */
const isAccountArea = computed(() => route.meta?.area === 'account')
/** Tunnel de souscription lancé par un adhérent connecté : rendu dans le chrome
 *  de l'espace perso (voir ProductView) → header public masqué également. */
const isConnectedFunnel = computed(
  () => mounted.value && isAuthenticated.value && !!route.meta?.productKey,
)
const showPublicHeader = computed(() => !isAccountArea.value && !isConnectedFunnel.value)

useHead({
  htmlAttrs: { lang: computed(() => locale.value) },
  link: [
    { rel: 'alternate', hreflang: 'fr', href: '/' },
    { rel: 'alternate', hreflang: 'en', href: '/en/' },
    { rel: 'alternate', hreflang: 'x-default', href: '/' },
  ],
})
</script>

<template>
  <a href="#main" class="skip-link">Aller au contenu</a>
  <TopBar />
  <AppHeader v-if="showPublicHeader" />
  <router-view />
  <AppFooter />
  <ReassuranceDialog />
</template>
